import React, { useEffect } from 'react';
import Modal from '@/shared/components/Modal';
import ClientProfile from './ClientProfile';
import { useLazyQuery } from '@apollo/client';
import { GET_CLIENT } from '../queries';
import { QueryResult, QueryClientArgs } from '@/shared/types/generated';
import { FullScreenLoader } from './FullScreenLoader';
import useStore from '@/stores/useStore';
import SentryService from '@/services/sentry/Sentry.service';

interface ClientProfileModalProps {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  clientId: number;
}

const ClientProfileModal: React.FC<ClientProfileModalProps> = ({
  isVisible,
  setIsVisible,
  clientId,
}) => {
  const { setSnackbarState } = useStore();
  const [getData, { data, loading }] = useLazyQuery<
    QueryResult<'client'>,
    QueryClientArgs
  >(GET_CLIENT, {
    onError: (error) => {
      SentryService.captureError('Error fetching client data', error);
      setSnackbarState({
        message: error.message,
        severity: 'error',
        open: true,
      });
    },
  });

  useEffect(() => {
    if (clientId && isVisible) {
      getData({
        variables: {
          input: {
            id: clientId,
          },
        },
      });
    }
  }, [clientId, getData, isVisible]);

  if (loading) {
    return <FullScreenLoader />;
  }

  if (!data?.client?.user) {
    return null;
  }

  const { client } = data;
  const user = data.client.user;

  return (
    <Modal
      isVisible={isVisible}
      setIsVisible={setIsVisible}
      onRequestClose={() => setIsVisible(false)}
      onDismiss={() => setIsVisible(false)}
    >
      <ClientProfile
        clientName={`${user.firstName} ${user.lastName}`}
        aesthetics={client.aesthetics ?? []}
        profilePicUrl={user.profilePicUrl}
        bio={client.bio}
        sizingDetails={client.sizingDetails}
        height={client.height}
        city={user.address?.city}
        state={user.address?.state}
        birthDate={user.birthDate}
        shareAge={client.shareAge}
        clientUserId={user.id}
        clientId={client.id}
        onClose={() => setIsVisible(false)}
        isModal
      />
    </Modal>
  );
};

export default ClientProfileModal;
